// usePagination — the one client-side pagination helper. Give it the full row
// array and a page size; it returns the current page's slice plus everything the
// <Pagination> control needs. Page is derived-and-clamped (no effect), so when the
// underlying list shrinks (filtering, a row finishing and leaving the list) the
// effective page snaps back into range automatically.
import { useEffect, useMemo, useState } from "react";

export interface PaginationState<T> {
  /** 1-based effective page (already clamped to [1, pageCount]). */
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
  /** The rows to render for the current page. */
  pageItems: T[];
  /** 1-based index of the first/last item shown (0 when empty). */
  rangeStart: number;
  rangeEnd: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export function usePagination<T>(
  items: T[],
  initialPageSize = 25
): PaginationState<T> {
  const [rawPage, setRawPage] = useState(1);
  const [pageSize, setRawPageSize] = useState(initialPageSize);

  // Track a changing page size (e.g. a viewport-derived one). The page is
  // derived-and-clamped below, so growing the size never needs an explicit page
  // reset. A no-op for callers that pass a stable literal.
  useEffect(() => {
    setRawPageSize(initialPageSize);
  }, [initialPageSize]);

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, rawPage), pageCount);
  const start = (page - 1) * pageSize;

  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize]
  );

  return {
    page,
    pageSize,
    pageCount,
    total,
    pageItems,
    rangeStart: total === 0 ? 0 : start + 1,
    rangeEnd: Math.min(start + pageSize, total),
    setPage: (p: number) => setRawPage(Math.max(1, Math.floor(p))),
    setPageSize: (s: number) => {
      setRawPageSize(s);
      setRawPage(1);
    },
  };
}
